import React from 'react';
import { Box, Stack, Switch, Slider, Typography, CircularProgress } from '@mui/material';

export interface LayerConfig {
  id: string;
  label: string;
  visible: boolean;
  opacity: number;   // 0.0–1.0
  loading: boolean;
}

interface LayerPanelProps {
  layers: LayerConfig[];
  onToggle: (id: string, visible: boolean) => void;
  onOpacityChange: (id: string, opacity: number) => void;
}

const LayerPanel: React.FC<LayerPanelProps> = ({ layers, onToggle, onOpacityChange }) => {
  return (
    <Box>
      {/* Title */}
      <Typography
        variant="overline"
        sx={{
          color: 'rgba(255,255,255,0.5)',
          letterSpacing: 1.5,
          fontSize: '0.65rem',
          display: 'block',
          mb: 0.5,
        }}
      >
        Data Layers
      </Typography>

      {/* Layer rows */}
      <Stack spacing={0.5}>
        {layers.map((layer) => (
          <Box key={layer.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Toggle Switch */}
            <Switch
              checked={layer.visible}
              onChange={(_event, checked) => {
                onToggle(layer.id, checked);
              }}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: 'white' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'rgba(255,255,255,0.5)',
                },
                '& .MuiSwitch-track': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                },
              }}
            />

            {/* Layer label */}
            <Typography
              variant="body2"
              sx={{
                color: layer.visible ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
                fontSize: '0.8rem',
                flex: 1,
                minWidth: 80,
              }}
            >
              {layer.label}
            </Typography>

            {/* Opacity Slider */}
            <Slider
              value={layer.opacity * 100}
              onChange={(_event, value) => {
                onOpacityChange(layer.id, (value as number) / 100);
              }}
              min={0}
              max={100}
              step={1}
              disabled={!layer.visible}
              sx={{
                color: 'white',
                width: 80,
                '& .MuiSlider-thumb': {
                  width: 10,
                  height: 10,
                  backgroundColor: 'white',
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 5px rgba(255, 255, 255, 0.16)',
                  },
                },
                '& .MuiSlider-track': { height: 2 },
                '& .MuiSlider-rail': { height: 2, opacity: 0.3 },
              }}
            />

            {/* Loading indicator */}
            {layer.loading && (
              <CircularProgress size={14} sx={{ color: 'white', flexShrink: 0 }} />
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default LayerPanel;
